import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/hooks/useTenant";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useStaffTeamMemberId } from "@/hooks/useStaffTeamMember";
import { Clock, Archive } from "lucide-react";
import { CustomerSearchInput } from "./CustomerSearchInput";
import { Customer } from "@/hooks/useCustomers";
import { isPaused } from "@/lib/queries/teamMembers";

import { z } from "zod";

const VisitFormDataSchema = z.object({
  customer_id: z.string(),
  appointment_type_id: z.string(),
  team_member_id: z.string(),
  resource_ids: z.array(z.string()),
  date: z.string(),
  start_time: z.string(),
  duration: z.number(),
  notes: z.string(),
});

export type VisitFormData = z.infer<typeof VisitFormDataSchema>;

interface VisitFormContentProps {
  values: VisitFormData;
  onFieldChange: (field: keyof VisitFormData, value: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  showNoShowButton?: boolean;
  onNoShow?: () => void;
  currentStatus?: string | null;
  selectedCustomer?: Customer | null;
  onCustomerSelect?: (customer: Customer | null) => void;
  visitServiceIds: string[];
  visitTeamMemberIds: string[];
  onVisitChange: (serviceIds: string[], teamMemberIds: string[]) => void;
  filteredTeamMemberIds?: string[];
  prefilledCustomer?: Customer;
  isEditingVisit?: boolean;
}

export const VisitFormContent = ({
  values,
  onFieldChange,
  onSubmit,
  onCancel,
  isLoading,
  showDeleteButton = false,
  onDelete,
  showNoShowButton = false,
  onNoShow,
  currentStatus = null,
  onCustomerSelect = () => { },
  visitServiceIds,
  visitTeamMemberIds,
  onVisitChange,
  prefilledCustomer,
  isEditingVisit = false
}: VisitFormContentProps) => {
  const { t } = useTranslation();
  const { currentTenant, allServices, teamMembers } = useTenant();
  const isStaff = useIsStaff();
  const staffTeamMemberId = useStaffTeamMemberId();

  // Staff may only ever assign appointments to themselves. Keep every slot's
  // team member pinned to the staff user (the picker is also disabled below, and
  // the visit edge functions reject anything else server-side).
  useEffect(() => {
    // Only pin to self for NEW appointments. When editing (e.g. a colleague's
    // appointment opened from the read-only whole-salon calendar) we must NOT
    // rewrite the assignee — that edit is read-only for staff (blocked below +
    // server-side).
    if (isEditingVisit || !isStaff || !staffTeamMemberId || visitServiceIds.length === 0) return;
    if (visitTeamMemberIds.every((id) => id === staffTeamMemberId)) return;
    onVisitChange(visitServiceIds, visitServiceIds.map(() => staffTeamMemberId));
    onFieldChange("team_member_id", staffTeamMemberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, staffTeamMemberId, visitServiceIds, visitTeamMemberIds, isEditingVisit]);

  // A staff member viewing an appointment that isn't theirs (only reachable via
  // the read-only whole-salon calendar) — the form is read-only for them.
  const staffReadOnly = isStaff && isEditingVisit && !!staffTeamMemberId &&
    !visitTeamMemberIds.includes(staffTeamMemberId);

  // Track whether different team members should be assigned per appointment
  const [useDifferentTeamMembers, setUseDifferentTeamMembers] = useState(() => {
    if (isEditingVisit && visitTeamMemberIds.length > 1) {
      return visitTeamMemberIds.some((id, idx) => idx > 0 && id !== visitTeamMemberIds[0]);
    }
    return false;
  });

  // Create a list of services to show in dropdowns:
  // - Non-deleted services (always shown)
  // - Currently selected services even if deleted (so user can see what's assigned)
  const availableServices = useMemo(() => {
    const nonDeletedServices = allServices.filter(s => !s.deleted_at);
    const selectedDeletedServices = allServices.filter(s =>
      s.deleted_at && visitServiceIds.includes(s.id)
    );
    return [...selectedDeletedServices, ...nonDeletedServices];
  }, [allServices, visitServiceIds]);

  // Team members selectable for a NEW/edited internal appointment.
  // Paused members are hidden so nobody gets booked onto them internally,
  // but a member already assigned to this visit stays selectable so editing
  // an existing appointment never loses its assignment.
  const selectableTeamMembers = useMemo(() => {
    return teamMembers.filter(
      (m) => !isPaused(m) || visitTeamMemberIds.includes(m.id),
    );
  }, [teamMembers, visitTeamMemberIds]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };


  const handleVisitServiceChange = (idx: number, value: string) => {
    const nextServices = [...visitServiceIds];
    nextServices[idx] = value;
    const nextMembers = [...visitTeamMemberIds];

    // Keep team member assignment regardless of service relationship
    // The UI will show a warning if the relationship doesn't exist

    onVisitChange(nextServices, nextMembers);

    if (idx === 0) {
      onFieldChange('appointment_type_id', value);
      onFieldChange('team_member_id', nextMembers[0] || "");
    }
  };

  const handleVisitTeamMemberChange = (idx: number, value: string) => {
    const nextMembers = [...visitTeamMemberIds];
    nextMembers[idx] = value;
    onVisitChange(visitServiceIds, nextMembers);
    if (idx === 0) {
      onFieldChange('team_member_id', value);
    }
  };

  // Handler for single team member selection (applies to all appointments)
  const handleSingleTeamMemberChange = (value: string) => {
    const nextMembers = visitServiceIds.map(() => value);
    onVisitChange(visitServiceIds, nextMembers);
    onFieldChange('team_member_id', value);
  };

  const handleRemoveVisitService = (idx: number) => {
    const nextServices = visitServiceIds.filter((_, i) => i !== idx);
    const nextMembers = visitTeamMemberIds.filter((_, i) => i !== idx);
    onVisitChange(nextServices, nextMembers);
    if (nextServices.length > 0) {
      onFieldChange('appointment_type_id', nextServices[0]);
      onFieldChange('team_member_id', nextMembers[0]);
    }
  };

  const handleAddService = () => {
    // When using shared team members, use the existing team member for the new appointment
    const newTeamMemberId = useDifferentTeamMembers ? "" : (visitTeamMemberIds[0] || "");
    onVisitChange([...visitServiceIds, ""], [...visitTeamMemberIds, newTeamMemberId]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date">{t("date")} <span className="text-red-500">*</span></Label>
          <Input
            id="date"
            type="date"
            value={values.date}
            onChange={(e) => onFieldChange('date', e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="time" className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4" />
            {t("time")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="time"
            type="time"
            value={values.start_time}
            onChange={(e) => onFieldChange('start_time', e.target.value)}
            required
          />
        </div>
      </div>

      {/* Customer Selection */}
      <CustomerSearchInput
        value={values.customer_id}
        onCustomerSelect={(customer) => {
          if (customer) {
            onFieldChange('customer_id', customer.id);
            onCustomerSelect(customer);
          } else {
            onFieldChange('customer_id', "");
            onCustomerSelect(null);
          }
        }}
        tenantId={currentTenant?.id || null}
        required
        isEditing={showDeleteButton}
        disabled={!!prefilledCustomer}
      />

      {/* Service and Team Member Selection */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="service">{t("service")} <span className="text-red-500">*</span></Label>
        </div>

        {(!showDeleteButton || isEditingVisit) && (
          <div className="space-y-3">
            {visitServiceIds.map((serviceId, idx) => {
              const teamMemberId = visitTeamMemberIds[idx] || "";

              return (
                <div key={idx} className="flex items-start gap-2">
                  <div className="flex-1">
                    <Select
                      value={serviceId}
                      onValueChange={(value) => handleVisitServiceChange(idx, value)}
                      required
                    >
                      <SelectTrigger id={`service-${idx}`} className={!serviceId ? "border-red-200" : ""} data-testid={idx === 0 ? "service-select" : undefined}>
                        <SelectValue placeholder={t("selectService")}>
                          {serviceId && (() => {
                            const selectedType = allServices?.find(type => type.id === serviceId);
                            return selectedType ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: selectedType.color || '#3b82f6' }} />
                                <span>{selectedType.title} ({selectedType.duration} min)</span>
                                {selectedType.deleted_at && (
                                  <Badge variant="destructive" className="text-xs">
                                    <Archive className="h-3 w-3 mr-1" />
                                    {t('deleted')}
                                  </Badge>
                                )}
                              </div>
                            ) : null;
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableServices?.map((type) => (
                          <SelectItem key={type.id} value={type.id} data-value={type.id}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: type.color || '#3b82f6' }} />
                              <span>{type.title} ({type.duration} min)</span>
                              {type.deleted_at && (
                                <Badge variant="destructive" className="text-xs">
                                  <Archive className="h-3 w-3 mr-1" />
                                  {t('deleted')}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {useDifferentTeamMembers && (
                      <div className="mt-2 flex items-start gap-2">
                        {/* Tree connector - L shape */}
                        <div className="flex items-end" style={{ width: '20px', height: '20px', marginTop: '10px' }}>
                          <div style={{
                            borderLeft: '1px solid rgb(203 213 225)',
                            borderBottom: '1px solid rgb(203 213 225)',
                            width: '20px',
                            height: '20px'
                          }}></div>
                        </div>
                        <div className="flex-1 relative">
                          <Select
                            value={teamMemberId}
                            onValueChange={(value) => handleVisitTeamMemberChange(idx, value)}
                            required
                            disabled={isStaff}
                          >
                            <SelectTrigger id={`teamMember-${idx}`} className={!teamMemberId ? "border-red-200" : ""} data-testid={idx === 0 ? "team-member-select" : undefined}>
                              <SelectValue placeholder={t("selectTeamMember")} />
                            </SelectTrigger>
                            <SelectContent>
                              {selectableTeamMembers.map((member) => (
                                <SelectItem key={member.id} value={member.id} data-value={member.id}>
                                  {member.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                  {visitServiceIds.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveVisitService(idx)}
                      className="mt-0"
                    >
                      ×
                    </Button>
                  )}
                </div>
              );
            })}

            {/* Add service button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleAddService}
              disabled={visitServiceIds.some(id => !id)}
            >
              {t('addService')}
            </Button>

            {/* Single team member dropdown when not using different team members */}
            {!useDifferentTeamMembers && visitServiceIds.length > 0 && (
              <div>
                <Label htmlFor="single-team-member">{t("teamMember")} <span className="text-red-500">*</span></Label>
                <Select
                  value={visitTeamMemberIds[0] || ""}
                  onValueChange={handleSingleTeamMemberChange}
                  required
                  disabled={isStaff}
                >
                  <SelectTrigger id="single-team-member" className={!visitTeamMemberIds[0] ? "border-red-200" : ""} data-testid="team-member-select">
                    <SelectValue placeholder={t("selectTeamMember")} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableTeamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id} data-value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Checkbox to enable different team members (not for staff — own only) */}
            {!isStaff && visitServiceIds.length > 1 && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="different-team-members"
                  checked={useDifferentTeamMembers}
                  onCheckedChange={(checked) => setUseDifferentTeamMembers(checked === true)}
                />
                <label
                  htmlFor="different-team-members"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('assignDifferentTeamMembers')}
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes">{t("notes")} ({t("optional")})</Label>
        <Textarea
          id="notes"
          value={values.notes}
          onChange={(e) => onFieldChange('notes', e.target.value)}
          placeholder={t("appointmentNotesPlaceholder")}
          rows={3}
        />
      </div>

      {/* Action buttons */}
      <div className="flex justify-between pt-4">
        {showDeleteButton && onDelete && (
          <Button
            type="button"
            variant={showNoShowButton ? "outline" : "destructive"}
            onClick={showNoShowButton ? onNoShow : onDelete}
            disabled={isLoading}
            className={showNoShowButton ? "text-orange-600 border-orange-300 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-700" : ""}
          >
            {showNoShowButton
              ? (currentStatus === 'no_show' ? t("unmarkNoShow") : t("markAsNoShow"))
              : t("delete")
            }
          </Button>
        )}
        <div className="flex space-x-2 ml-auto">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("cancel")}
          </Button>
          {!staffReadOnly && (
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (showDeleteButton ? t("updating") : t("creating")) : (showDeleteButton ? t("update") : t("create"))}
            </Button>
          )}
        </div>
        {staffReadOnly && (
          <p className="text-xs text-muted-foreground text-right">{t("staffLogin.readOnlyAppointment")}</p>
        )}
      </div>
    </form>
  );
};
